/**
 * The version block at the foot of the sidebar.
 *
 * The mockup shows this offering an update: "v0.8.1 hazır" with a Güncelle
 * button. **There is no updater.** No release feed, no download path, no
 * install mechanism - so a button here would do nothing, and a version
 * claiming a newer build is available would be inventing a fact about the
 * outside world.
 *
 * What it does instead is answer the question this deployment actually raises:
 * *is the site running what was last pushed?* The API reports a build stamp
 * that moves with every deployed change, and the moment its own process
 * started. Between them, a stale deployment is visible at a glance rather than
 * inferred from a feature that did not appear.
 *
 * Everything here degrades: an API that predates the build stamp simply shows
 * the version, as before.
 */

import { useCallback } from "react";
import { getHealth } from "@/api/client";
import { formatDateTime } from "@/lib/format";
import { useAsync } from "@/lib/useAsync";

export function VersionCard() {
  const fetcher = useCallback((signal: AbortSignal) => getHealth({ signal }), []);
  const { state } = useAsync(fetcher);

  // Until the API answers, the version is unknown - and an unknown version is
  // shown as unknown, not as a guess or a bundled constant that would drift
  // from whatever is actually running.
  const health = state.status === "success" ? state.data : null;
  const version = health?.version ?? null;
  const build = health?.build ?? null;

  return (
    <div className="version-card">
      <p className="version-card__name">
        SentyCap {version ? `v${version}` : "—"}
        {build ? <span className="version-card__build">yapı {build}</span> : null}
      </p>
      {/*
        The server's start time used to be here, at the foot of the navigation
        menu. It answers an operator's question, not a customer's, and on a
        phone it was the last thing in the drawer - a line about process
        uptime under the links somebody opened the menu to use. It moved to
        Hakkında, where the rest of the deployment's own facts live.
      */}
    </div>
  );
}

/**
 * When this deployment's API process started.
 *
 * Kept, because it is what makes a stale deployment visible at a glance rather
 * than inferred from a feature that did not appear. Shown where somebody goes
 * looking for it.
 */
export function ServerStartedAt() {
  const fetcher = useCallback((signal: AbortSignal) => getHealth({ signal }), []);
  const { state } = useAsync(fetcher);
  const startedAt = state.status === "success" ? state.data.started_at : null;

  return (
    <p className="muted">
      {startedAt
        ? `Sunucu ${formatDateTime(startedAt)} tarihinde başlatıldı.`
        : "Sunucu başlangıç zamanı okunamadı."}
    </p>
  );
}
